// hooks/useTransactions.js
import { useState, useCallback, useRef } from 'react';

const useTransactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Conserver les derniers filtres utilisés afin que les appels sans paramètres les réutilisent
    const lastFiltersRef = useRef({});

    const fetchTransactions = useCallback(async (filters) => {
        // Règle: si un argument est fourni (même un objet vide), on l'utilise tel quel et on met à jour le cache.
        // Si aucun argument n'est fourni (undefined), on réutilise les derniers filtres connus.
        const isArgumentProvided = typeof filters !== 'undefined';
        const effectiveFilters = isArgumentProvided
            ? (filters || {})
            : (lastFiltersRef.current || {});

        // Mettre à jour le cache des filtres si un argument est fourni (y compris un objet vide pour signifier aucun filtre)
        if (isArgumentProvided) {
            lastFiltersRef.current = filters || {};
        }

        setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams();

            if (effectiveFilters.startDate) queryParams.append('startDate', effectiveFilters.startDate);
            if (effectiveFilters.endDate) queryParams.append('endDate', effectiveFilters.endDate);
            if (effectiveFilters.type) queryParams.append('type', effectiveFilters.type);
            if (effectiveFilters.categoryId) queryParams.append('categoryId', effectiveFilters.categoryId);
            if (effectiveFilters.accountId) queryParams.append('accountId', effectiveFilters.accountId);

            const qs = queryParams.toString();
            const url = qs ? `/api/transactions?${qs}` : '/api/transactions';

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                setTransactions(data.data);
            } else {
                setError(data.message || 'Erreur lors du chargement');
            }
        } catch (err) {
            setError('Erreur de connexion');
            console.error('Erreur fetch transactions:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteTransaction = useCallback(async (transactionId) => {
        try {
            const response = await fetch(`/api/transactions/${transactionId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                setTransactions(prev => prev.filter(t => t._id !== transactionId));
                return { success: true, message: data.message };
            } else {
                return { success: false, message: data.message };
            }
        } catch (err) {
            return { success: false, message: 'Erreur lors de la suppression' };
        }
    }, []);

    return {
        transactions,
        loading,
        error,
        fetchTransactions,
        deleteTransaction
    };
};

export default useTransactions;
